import { useCallback, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import appConfig from 'app-config';
import SubscriptionGuideModal, { SUBSCRIPTION_GUIDE_LAND_FROM, SubscriptionGuideLandFrom } from 'src/components/AzarOnly/Subscription/SubscriptionGuideModal';
import PwaGuideModal from 'src/components/PwaGuideModal';
import ShopModal from 'src/components/Shop/ShopModal';
import GenderPreferenceModal from 'src/components/VividiOnly/Match/Preference/Gender/Modal';
import useCallbackWithCheckLogin from 'src/hooks/useCallbackWithCheckLogin';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import useEnableRemoteConfig from 'src/hooks/useEnableRemoteConfig';
import { Routes } from 'src/router/route';
import { historyTabAtom } from 'src/stores/history/atom';
import { showModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { DEEPLINK_FRAGMENT_PATH } from 'src/utils/deeplink';
function parseHash(hash: string) {
  const cleaned = hash.replace(/^#/, '');
  const [path, queryString] = cleaned.split('?');
  const params = new URLSearchParams(queryString || '');
  return {
    path,
    params
  };
}
export default function useUrlFragmentWebLink() {
  const showModal = useSetAtom(showModalAtom);
  const setHistoryTab = useSetAtom(historyTabAtom);
  const router = useRouter();
  const device = useCssMediaDevice();
  const enableViewedMe = useEnableRemoteConfig('enableViewedMe');
  const showGenderModal = useCallback(() => {
    showModal({
      key: ModalType.MATCH_SETTING,
      component: () => <GenderPreferenceModal landFrom='MIRROR' onSave={() => null} />
    });
  }, [showModal]);
  const showShopModal = useCallback(() => {
    showModal({
      key: ModalType.SHOP,
      component: () => <ShopModal />
    });
  }, [showModal]);
  const showSubscriptionGuideModal = useCallback((pageId?: string, landFrom?: SubscriptionGuideLandFrom) => {
    showModal({
      key: ModalType.SUBSCRIPTION_GUIDE,
      component: () => <SubscriptionGuideModal defaultPageId={pageId} landFrom={landFrom ?? SUBSCRIPTION_GUIDE_LAND_FROM.MIRROR_HEADER_BANNER} />
    });
  }, [showModal]);
  const showGenderModalWithCheckLogin = useCallbackWithCheckLogin(showGenderModal);
  const showShopModalWithCheckLogin = useCallbackWithCheckLogin(showShopModal);
  const showSubscriptionGuideWithCheckLogin = useCallbackWithCheckLogin(showSubscriptionGuideModal);
  const openMessageRoom = useCallback((messageThreadId: string) => {
    if (device === 'desktop') {
      router.push({
        ...Routes['/messages'](),
        query: {
          messageThreadId
        }
      });
    } else {
      router.push({
        ...Routes['/messages/room'](),
        query: {
          messageThreadId
        }
      });
    }
  }, [device, router]);
  const openFollow = useCallback((params: URLSearchParams) => {
    const tab = params.get('tab') || 'following';
    const filter = params.get('filter');
    if (device === 'desktop') {
      router.push({
        ...Routes['/messages'](),
        query: {
          followTab: tab,
          ...(filter && {
            filter
          })
        }
      });
    } else {
      router.push({
        ...Routes['/follow'](),
        query: {
          tab,
          ...(filter && {
            filter
          })
        }
      });
    }
  }, [device, router]);
  useEffect(() => {
    const handleHashChange = () => {
      const {
        path,
        params
      } = parseHash(window.location.hash);
      switch (path) {
        case DEEPLINK_FRAGMENT_PATH.GENDER:
          // gender hash는 Vividi 전용. 새 탭을 열 때 #gender hash가 URL에 붙는 경우를 차단
          if (appConfig.serviceCode === 'vividi') showGenderModalWithCheckLogin();
          break;
        case DEEPLINK_FRAGMENT_PATH.PWA_GUIDE:
          showModal({
            key: ModalType.PWA_GUIDE,
            component: PwaGuideModal
          });
          break;
        case DEEPLINK_FRAGMENT_PATH.SHOP:
          showShopModalWithCheckLogin();
          break;
        case DEEPLINK_FRAGMENT_PATH.MESSAGES:
          {
            const messageThreadId = params.get('messageThreadId');
            if (messageThreadId) openMessageRoom(messageThreadId);
            break;
          }
        case DEEPLINK_FRAGMENT_PATH.FOLLOWING:
          openFollow(new URLSearchParams([['tab', 'following'], ...params.entries()]));
          break;
        case DEEPLINK_FRAGMENT_PATH.FOLLOWER:
          openFollow(new URLSearchParams([['tab', 'follower'], ...params.entries()]));
          break;
        case DEEPLINK_FRAGMENT_PATH.SUBS_POPUP:
          {
            const pageId = params.get('pageId') ?? undefined;
            const landFrom = params.get('land_from') ?? undefined;
            showSubscriptionGuideWithCheckLogin(pageId, landFrom as SubscriptionGuideLandFrom);
            break;
          }
        case DEEPLINK_FRAGMENT_PATH.VIEWED_ME:
        case DEEPLINK_FRAGMENT_PATH.PROFILE_VIEWER:
          // viewed_me의 legacy 대응 https://hpcnt.slack.com/archives/C01BHEXJS3Z/p1777448715006549?thread_ts=1777369838.381639&cid=C01BHEXJS3Z
          if (enableViewedMe === undefined) return;
          if (enableViewedMe === true) {
            setHistoryTab('viewed-me');
            router.push(Routes['/history']());
          } else {
            router.push(Routes['/']());
          }
          break;
        case DEEPLINK_FRAGMENT_PATH.HISTORY:
          router.push(Routes['/history']());
          break;
        case DEEPLINK_FRAGMENT_PATH.RECENT_ACTIVITY:
          setHistoryTab('recent-activity');
          router.push(Routes['/history']());
          break;
        default:
          break;
      }
      if (path) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [showGenderModalWithCheckLogin, showShopModalWithCheckLogin, showSubscriptionGuideWithCheckLogin, openMessageRoom, openFollow, setHistoryTab, showModal, enableViewedMe]);
}